import { Box, Container, Grid, Typography, SvgIcon } from '@material-ui/core';
import RoomIcon from '@material-ui/icons/Room';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { css } from '@emotion/react';

import { SportIcon, getSportComponent } from 'lib/sports';

import { useColors } from 'hooks/useColors';
import { IColors } from 'types/colors';
import { mobileOnly, tabletUp, desktopUp, setOpacity, fullMaxWidthCss } from 'styles/utils';

import ExpandableText from 'components/shared/ExpandableText/ExpandableText';
import Link from 'components/shared/Link/Link';
import { RCAddress } from 'types/coreEntites/Address';
import { useEffect, useState } from 'react';

const heroCss = (colors: IColors) => css`
	${tabletUp} {
		color: ${colors.tx_white};

		a {
			color: ${colors.tx_white};
			text-decoration: underline;
		}

		padding-top: 50px;
	}

	${mobileOnly} {
		color: ${colors.tx_text_primary};
	}

	min-height: 450px;
	margin-bottom: 30px;
`;

const heroTitleCss = css`
	font-size: 4rem;
	margin-bottom: 10px;
`;

const heroBackLinkCss = (colors: IColors) => css`
	a {
		display: flex;
		align-items: flex-end;

		${tabletUp} {
			color: ${colors.tx_white};
		}

		${desktopUp} {
			color: ${colors.tx_white};
		}
	}
`;

const heroTextCss = css`
	font-size: 1.4rem;
	font-weight: bold;

	&.MuiTypography-subtitle1 {
		margin-bottom: 5px;
	}
`;

const alignmentCss = css`
	vertical-align: middle;
	align-self: center;
`;

const sportsIconCss = css`
	.MuiSvgIcon-root {
		width: 30px;
		height: 30px;
		margin-right: 15px;
	}
`;

const tagCss = (colors: IColors) => css`
	background: ${colors.bg_background_primary};
	border-radius: 2px;
	font-family: Montserrat;
	color: ${colors.tx_text_primary};
	font-size: 1.2rem;
	font-weight: 600;
	text-align: center;
	padding: 2px 6px;
	margin-right: 6px;
`;

const heroDescriptionCss = (colors: IColors) => css`
	background-color: ${setOpacity(colors.opacityBackground || '', 0.79)};
	padding: 15px;
	margin-top: 20px;
	color: ${colors.tx_white};
`;

interface DescriptionProps {
	title?: string;
	description?: string;
	address?: RCAddress;
	backHref?: string;
	backTitle?: string;
	sports?: SportIcon[];
	maxSportsIcons?: number;
	tags?: string[];
}

function Hero({ children }: { children: React.ReactNode }) {
	const { colors } = useColors();
	return (
		<Container css={heroCss(colors)} maxWidth="lg">
			<Grid container justify="space-between" spacing={4}>
				{children}
			</Grid>
		</Container>
	);
}

function Summary({
	title,
	description,
	address,
	backHref,
	backTitle,
	sports = [],
	maxSportsIcons = 9,
	tags,
}: DescriptionProps) {
	const { colors } = useColors();

	const [sportsToDisplayList, setSportsToDisplayList] = useState<SportIcon[]>([]);

	useEffect(() => {
		if (sports?.length > maxSportsIcons) {
			setSportsToDisplayList(sports.slice(0, maxSportsIcons));
		} else {
			setSportsToDisplayList(sports);
		}
	}, [maxSportsIcons, sports]);

	return (
		<Grid css={fullMaxWidthCss} item sm={12} md={5}>
			{backHref && (
				<div css={heroBackLinkCss(colors)}>
					<Link href={backHref}>
						<ArrowBackIcon />
						{backTitle}
					</Link>
				</div>
			)}
			{title && <h1  data-testid="Hero-BC3450" css={heroTitleCss}>{title}</h1>}
			{address && (
				<Typography variant="subtitle1" css={heroTextCss}>
					{address.state && <RoomIcon fontSize="small" css={alignmentCss} />}
					<span css={alignmentCss}>
						{address.streetNum} {address.street}
						{address.street && ','} {address.city}
						{address.city && ','} {address.state}
						{address.state && ','} {address.zip}
					</span>
				</Typography>
			)}
			{sportsToDisplayList.length ? (
				<Box css={sportsIconCss} id="heroIcons">
					{sportsToDisplayList.map(sportItem => (
						<SvgIcon key={sportItem.sport} component={getSportComponent(sportItem.path)} viewBox="0 0 30 30" />
					))}
					{sports?.length > maxSportsIcons ? '...' : null}
					{tags?.length ? (
						<div id="heroTags">
							{tags?.map(value => (
								<span css={tagCss(colors)} key={value}>
									{value}
								</span>
							))}
						</div>
					) : null}
				</Box>
			) : null}
			{description && (
				<Box css={heroDescriptionCss(colors)}>
					<ExpandableText string={description} />
				</Box>
			)}
		</Grid>
	);
}

function Action({ children }: { children: React.ReactNode }) {
	return (
		<Grid item sm={12} md={7}>
			{children}
		</Grid>
	);
}

Hero.Summary = Summary;
Hero.Action = Action;

export default Hero;
